// ============================================================
// SevenStars SmartCare Passport V3 — Landing Page
// QR code is primary access. Name search for first-time access.
// ============================================================
import React, { useState } from 'react';
import { passportStore, customerStore, vehicleStore } from '../store/data';
import { Shield, Wrench, QrCode, Lock, ChevronRight, Phone, Search } from 'lucide-react';
import logo from '../logo.png';
import { QRCodeSVG } from 'qrcode.react';

export default function LandingPage({ onAdminLogin, onTechLogin, onUnitAccess }) {
  const [mode, setMode] = useState('home'); // home | findPassport | adminLogin | techLogin
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async (val) => {
    setSearch(val);
    if (val.length < 3) { setSearchResults([]); return; }
    const [allPassports, allVehicles, matchingCustomers] = await Promise.all([
      passportStore.getAll(),
      vehicleStore.getAll(),
      customerStore.searchAdmin(val),
    ]);
    const results = matchingCustomers.map(cust => {
      const p = allPassports.find(x => x.customer_id === cust.id);
      const v = p ? allVehicles.find(x => x.id === p.vehicle_id) : null;
      return p ? { c: cust, p, v } : null;
    }).filter(Boolean).slice(0, 8);
    setSearchResults(results);
  };

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    if (!onAdminLogin(password)) setError('Incorrect password.');
  };

  const handleTechSubmit = (e) => {
    e.preventDefault();
    if (!onTechLogin(password)) setError('Incorrect password.');
  };

  const getQrUrl = (token) =>
    `${window.location.origin}${window.location.pathname}?unit=${token}`;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #081d31 0%, #1B4F72 60%, #0d3a5c 100%)' }}>

      {/* Header */}
      <div className="px-5 pt-10 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-xl mb-5 p-2">
          <img src={logo} alt="SevenStars" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-white tracking-tight">SevenStars SmartCare</h1>
        <p className="text-sm font-semibold mt-1" style={{ color: '#E87722' }}>Service Passport · Romblon</p>
        <p className="text-white/50 text-xs mt-1">Official SEVENSTARS Dealer · Philippines</p>
      </div>

      <div className="flex-1 px-4 pb-10 max-w-md mx-auto w-full">

        {/* ── HOME ──────────────────────────────────────────── */}
        {mode === 'home' && (
          <div className="space-y-4">
            {/* QR instruction card */}
            <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(232,119,34,0.12)', border: '1.5px solid rgba(232,119,34,0.35)' }}>
              <QrCode size={36} className="mx-auto mb-3" style={{ color: '#E87722' }} />
              <h2 className="text-white font-bold text-base mb-1">Scan Your QR Code</h2>
              <p className="text-white/65 text-sm leading-relaxed">
                Use your phone camera to scan the QR code on your SevenStars e-bike or warranty card to instantly open your SmartCare Passport.
              </p>
            </div>

            {/* Find My Passport — name search for first-time access */}
            <button
              onClick={() => { setMode('findPassport'); setSearch(''); setSearchResults([]); }}
              className="w-full text-white rounded-2xl p-4 flex items-center gap-4 transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #E87722, #d46a1b)' }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <Search size={20} className="text-white" />
              </div>
              <div className="text-left flex-1">
                <div className="font-bold">Find My Passport</div>
                <div className="text-sm opacity-85">Don't have QR yet? Search your name</div>
              </div>
              <ChevronRight size={18} className="opacity-70" />
            </button>

            {/* Staff access */}
            <div className="space-y-3 pt-2">
              <p className="text-white/35 text-xs text-center uppercase tracking-widest">Staff Access</p>

              <button
                onClick={() => { setMode('adminLogin'); setError(''); setPassword(''); }}
                className="w-full rounded-2xl p-4 flex items-center gap-4 transition-all active:scale-95 border border-white/10"
                style={{ background: 'rgba(255,255,255,0.06)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Shield size={20} className="text-white/80" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-white">Admin Dashboard</div>
                  <div className="text-sm text-white/45">Register units · Manage records</div>
                </div>
                <ChevronRight size={18} className="text-white/25" />
              </button>

              <button
                onClick={() => { setMode('techLogin'); setError(''); setPassword(''); }}
                className="w-full rounded-2xl p-4 flex items-center gap-4 transition-all active:scale-95 border border-white/10"
                style={{ background: 'rgba(255,255,255,0.06)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Wrench size={20} className="text-white/80" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-white">Technician View</div>
                  <div className="text-sm text-white/45">Service schedule · Job status</div>
                </div>
                <ChevronRight size={18} className="text-white/25" />
              </button>
            </div>

            <div className="text-center pt-4 space-y-1">
              <p className="text-white/25 text-xs">SevenStars E-Bike · Official SEVENSTARS Dealer</p>
              <p className="text-white/35 text-xs flex items-center justify-center gap-1">
                <Phone size={11} /> 0999 341 4528
              </p>
            </div>
          </div>
        )}

        {/* ── FIND MY PASSPORT ──────────────────────────────── */}
        {mode === 'findPassport' && (
          <div className="space-y-4">
            <button onClick={() => setMode('home')} className="text-sm flex items-center gap-1" style={{ color: '#E87722' }}>
              ← Back
            </button>

            <div className="bg-white rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#1B4F72' }}>
                  <Search size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800">Find My Passport</h2>
                  <p className="text-slate-400 text-xs">Type your name to find your passport</p>
                </div>
              </div>

              <input
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 mb-1"
                placeholder="e.g. Lovely or Jean"
                value={search}
                onChange={e => handleSearch(e.target.value)}
                autoFocus
              />
              {search.length > 0 && search.length < 3 && (
                <p className="text-slate-400 text-xs px-1">Type at least 3 characters…</p>
              )}

              {/* Results — shows each person ONLY their own passport */}
              {searchResults.length > 0 && (
                <div className="space-y-2 mt-4">
                  {searchResults.map(({ c, p, v }) => (
                    <button
                      key={p.id}
                      onClick={() => onUnitAccess(p.token)}
                      className="w-full text-left p-4 rounded-xl border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-slate-800">{c.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {v?.model || 'SevenStars E-Bike'} · {c.municipality}
                          </div>
                          <div className="text-xs mt-1.5 font-semibold" style={{ color: '#1B4F72' }}>
                            Tap to open your passport →
                          </div>
                        </div>
                        <div className="bg-white p-1.5 rounded-xl border border-slate-100 ml-3 flex-shrink-0">
                          <QRCodeSVG value={getQrUrl(p.token)} size={46} fgColor="#1B4F72" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {search.length >= 3 && searchResults.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-slate-500 text-sm">No passport found for "<strong>{search}</strong>".</p>
                  <p className="text-slate-400 text-xs mt-1">Contact SevenStars to register your unit.</p>
                  <a href="tel:+639993414528" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold" style={{ color: '#1B4F72' }}>
                    <Phone size={13} /> 0999 341 4528
                  </a>
                </div>
              )}
            </div>

            {/* Tip */}
            <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-white/55 text-xs text-center leading-relaxed">
                💡 Once you receive your personal QR card from SevenStars, scan it directly with your phone camera — no searching needed!
              </p>
            </div>
          </div>
        )}

        {/* ── STAFF LOGIN ───────────────────────────────────── */}
        {(mode === 'adminLogin' || mode === 'techLogin') && (
          <div className="space-y-4">
            <button onClick={() => setMode('home')} className="text-sm flex items-center gap-1" style={{ color: '#E87722' }}>
              ← Back
            </button>
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#1B4F72' }}>
                  <Lock size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800">{mode === 'adminLogin' ? 'Admin Login' : 'Technician Login'}</h2>
                  <p className="text-slate-400 text-xs">SevenStars SmartCare</p>
                </div>
              </div>
              <form onSubmit={mode === 'adminLogin' ? handleAdminSubmit : handleTechSubmit}>
                <label className="block text-sm font-medium text-slate-600 mb-1">Password</label>
                <input
                  type="password"
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 mb-3"
                  placeholder="Enter password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  autoFocus
                />
                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <button type="submit" className="w-full text-white font-semibold py-3 px-6 rounded-xl transition-all" style={{ background: '#1B4F72' }}>
                  Enter {mode === 'adminLogin' ? 'Dashboard' : 'Tech View'}
                </button>
                <p className="text-slate-400 text-xs mt-3 text-center">
                  Contact SevenStars admin for access credentials.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
